import { css } from 'styled-components';

import { rem } from '../../tokens/spacing';
import type { ButtonSize } from './buttonTypes';

/** Uniform border width so filled and outline variants share the same outer box. */
export const BUTTON_BORDER_WIDTH = rem(1);

export type ButtonLayoutMode = 'default' | 'icon-only' | 'link' | 'ghost';

interface GeometryPreset {
  minHeight: string;
  padY: string;
  padX: string;
  gap: string;
  fontSize: string;
  lineHeight: string;
  iconFont: string;
  iconBox: string;
}

function materialIconRailBox(p: Pick<GeometryPreset, 'iconFont' | 'iconBox'>) {
  return css`
    .material-symbols-outlined {
      font-size: ${p.iconFont};
      line-height: 1;
      width: ${p.iconBox};
      height: ${p.iconBox};
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
  `;
}

const defaultPresets: Record<ButtonSize, GeometryPreset> = {
  sm: {
    minHeight: rem(32),
    padY: rem(5),
    padX: rem(12),
    gap: rem(4),
    fontSize: rem(12),
    lineHeight: rem(18),
    iconFont: rem(16),
    iconBox: rem(16),
  },
  md: {
    minHeight: rem(40),
    padY: rem(8),
    padX: rem(16),
    gap: rem(6),
    fontSize: rem(16),
    lineHeight: rem(22),
    iconFont: rem(20),
    iconBox: rem(20),
  },
  lg: {
    minHeight: rem(48),
    padY: rem(12),
    padX: rem(20),
    gap: rem(8),
    fontSize: rem(16),
    lineHeight: rem(22),
    iconFont: rem(22),
    iconBox: rem(22),
  },
};

const iconOnlyPresets: Record<ButtonSize, GeometryPreset> = {
  sm: {
    minHeight: rem(32),
    padY: rem(6),
    padX: rem(6),
    gap: rem(0),
    fontSize: rem(12),
    lineHeight: rem(1),
    iconFont: rem(16),
    iconBox: rem(16),
  },
  md: {
    minHeight: rem(40),
    padY: rem(8),
    padX: rem(8),
    gap: rem(0),
    fontSize: rem(12),
    lineHeight: rem(1),
    iconFont: rem(20),
    iconBox: rem(20),
  },
  lg: {
    minHeight: rem(48),
    padY: rem(10),
    padX: rem(10),
    gap: rem(0),
    fontSize: rem(12),
    lineHeight: rem(1),
    iconFont: rem(24),
    iconBox: rem(24),
  },
};

const linkPresets: Record<
  ButtonSize,
  Pick<GeometryPreset, 'minHeight' | 'padY' | 'padX' | 'fontSize' | 'lineHeight' | 'gap'>
> = {
  sm: {
    minHeight: rem(32),
    padY: rem(4),
    padX: rem(4),
    gap: rem(4),
    fontSize: rem(12),
    lineHeight: rem(18),
  },
  md: {
    minHeight: rem(40),
    padY: rem(8),
    padX: rem(4),
    gap: rem(6),
    fontSize: rem(14),
    lineHeight: rem(20),
  },
  lg: {
    minHeight: rem(48),
    padY: rem(10),
    padX: rem(6),
    gap: rem(8),
    fontSize: rem(16),
    lineHeight: rem(22),
  },
};

export function buttonGeometryCss(size: ButtonSize, layout: ButtonLayoutMode) {
  if (layout === 'link') {
    const p = linkPresets[size];
    return css`
      min-height: ${p.minHeight};
      padding: ${p.padY} ${p.padX};
      column-gap: ${p.gap};
      font-size: ${p.fontSize};
      line-height: ${p.lineHeight};
      border-radius: var(--ui-radius-sm, ${rem(4)});
      justify-content: center;
      text-decoration: underline;
      text-underline-offset: ${rem(2)};
      font-weight: 700;
      background: transparent;
      box-shadow: none;

      .material-symbols-outlined {
        font-size: ${p.fontSize};
        line-height: 1;
        width: 1.25em;
        height: 1.25em;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      @media (max-width: 768px) {
        ${size === 'md' &&
        css`
          font-size: ${rem(13)};
          line-height: ${rem(18)};
        `}
      }
    `;
  }

  if (layout === 'ghost') {
    const d = defaultPresets[size];
    return css`
      min-height: ${d.minHeight};
      padding: ${d.padY} ${d.padX};
      column-gap: ${d.gap};
      font-size: ${d.fontSize};
      line-height: ${d.lineHeight};
      border-radius: var(--ui-radius-md, ${rem(6)});
      justify-content: center;
      background: transparent;
      box-shadow: none;

      ${materialIconRailBox(d)}

      @media (max-width: 768px) {
        ${size === 'md' &&
        css`
          font-size: ${rem(13)};
          line-height: ${rem(18)};
          padding: ${rem(9)} ${rem(15)};
        `}
      }
    `;
  }

  if (layout === 'icon-only') {
    const i = iconOnlyPresets[size];
    return css`
      min-height: ${i.minHeight};
      min-width: ${i.minHeight};
      width: ${i.minHeight};
      padding: ${i.padY};
      column-gap: ${i.gap};
      font-size: ${i.fontSize};
      line-height: ${i.lineHeight};
      border-radius: var(--ui-radius-md, ${rem(6)});
      justify-content: center;

      ${materialIconRailBox(i)}
    `;
  }

  const d = defaultPresets[size];
  return css`
    min-height: ${d.minHeight};
    padding: ${d.padY} ${d.padX};
    column-gap: ${d.gap};
    font-size: ${d.fontSize};
    line-height: ${d.lineHeight};
    border-radius: var(--ui-radius-md, ${rem(6)});
    justify-content: center;

    ${materialIconRailBox(d)}

    @media (max-width: 768px) {
      ${size === 'md' &&
      css`
        font-size: ${rem(13)};
        line-height: ${rem(18)};
        padding: ${rem(9)} ${rem(15)};
      `}
    }
  `;
}
